/**
 * À vista com desconto vs parcelado, com taxa de oportunidade mensal.
 * PV das parcelas = Σ parcela / (1+r)^i
 * Se PV_parcelas < valor à vista → parcelar deixa mais dinheiro no bolso (considerando rendimento).
 */
export function simularAvistaParcelado({
    valor,
    descontoPct = 0,
    numParcelas = 1,
    jurosMensalPct = 0,
    rentabilidadeMensalPct = 0.8,
}) {
    const v = Number(valor);
    const desc = Number(descontoPct) || 0;
    const n = Math.max(1, Math.floor(Number(numParcelas) || 1));
    const j = (Number(jurosMensalPct) || 0) / 100;
    const r = (Number(rentabilidadeMensalPct) || 0) / 100;

    if (!Number.isFinite(v) || v <= 0) return null;
    if (desc < 0 || desc >= 100) return null;

    const valorAvista = v * (1 - desc / 100);

    let parcela;
    let totalParcelado;
    if (j > 0) {
        // Tabela Price sobre o valor cheio (sem desconto à vista)
        const fator = (j * Math.pow(1 + j, n)) / (Math.pow(1 + j, n) - 1);
        parcela = v * fator;
        totalParcelado = parcela * n;
    } else {
        parcela = v / n;
        totalParcelado = v;
    }

    let valorPresenteParcelas = 0;
    if (r > 0) {
        for (let i = 1; i <= n; i += 1) {
            valorPresenteParcelas += parcela / Math.pow(1 + r, i);
        }
    } else {
        valorPresenteParcelas = totalParcelado;
    }

    const economiaAvistaVsTotal = totalParcelado - valorAvista;
    const vantagemPresente = valorPresenteParcelas - valorAvista;
    // vantagemPresente > 0 → à vista é melhor (PV do parcelado é maior)
    // vantagemPresente < 0 → parcelar é melhor

    let melhor;
    if (Math.abs(vantagemPresente) < 0.5) {
        melhor = 'empate';
    } else if (vantagemPresente > 0) {
        melhor = 'avista';
    } else {
        melhor = 'parcelado';
    }

    const descontoMinimoBreakEven = calcularDescontoMinimo(v, valorPresenteParcelas);

    return {
        valorAvista,
        parcela,
        totalParcelado,
        valorPresenteParcelas,
        economiaAvistaVsTotal,
        vantagemPresente,
        melhor,
        descontoMinimoBreakEven,
        numParcelas: n,
        veredicto: buildVeredicto(melhor, valorAvista, valorPresenteParcelas, totalParcelado, descontoMinimoBreakEven, desc),
    };
}

function calcularDescontoMinimo(valorCheio, pvParcelas) {
    if (!Number.isFinite(valorCheio) || valorCheio <= 0) return null;
    const min = ((valorCheio - pvParcelas) / valorCheio) * 100;
    return Math.max(0, min);
}

function buildVeredicto(melhor, valorAvista, pv, totalParcelado, descontoMin, descInformado) {
    if (melhor === 'empate') {
        return {
            tipo: 'empate',
            titulo: 'Tanto faz',
            detalhe: 'À vista e parcelado ficam praticamente iguais considerando o rendimento do dinheiro.',
        };
    }
    if (melhor === 'avista') {
        return {
            tipo: 'positivo',
            titulo: 'Vale pagar à vista',
            detalhe: `À vista sai ${valorAvista.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}. O parcelado, em valor presente, equivale a ${pv.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} (total pago ${totalParcelado.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}).`,
        };
    }
    const detalheExtra = descontoMin != null && descInformado < descontoMin
        ? ` O desconto à vista precisaria ser pelo menos ${descontoMin.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% para empatar.`
        : '';
    return {
        tipo: 'positivo',
        titulo: 'Vale parcelar',
        detalhe: `Deixando o dinheiro render, o parcelado equivale a ${pv.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} em valor presente, melhor que os ${valorAvista.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} à vista.${detalheExtra}`,
    };
}
